/**
 * components/landing/ai-platform/sections/KnowledgeBase.jsx — §4.5.
 *
 * Explains how the RAG stack works.  Left — sources tile grid.  Right —
 * an animated retrieval flow (query → embed → vector search → rerank
 * → grounded prompt → answer).
 */

import React from 'react'
import clsx from 'clsx'
import { FileText, BookOpen, LineChart, GraduationCap, FolderLock, ArrowRight, Sparkles, Database, MessageSquare } from 'lucide-react'
import { Container, Eyebrow, SectionHeading } from '@/components/landing/design-system'
import { RAG_SOURCES } from '../data'
import { TONE } from '../_shared'

const ICONS = { FileText, BookOpen, LineChart, GraduationCap, FolderLock }

export default function KnowledgeBase() {
  return (
    <section className="relative py-24 md:py-28">
      <div className="pointer-events-none absolute inset-0 -z-10 opacity-[0.4]"
        style={{ background: 'radial-gradient(50% 40% at 50% 20%, rgba(168,85,247,0.10), transparent 70%)' }} />
      <Container size="wide" className="flex flex-col gap-12">
        <div className="flex flex-col items-center text-center gap-5 max-w-3xl mx-auto">
          <Eyebrow>Knowledge Base · RAG</Eyebrow>
          <SectionHeading
            align="center"
            title={<>Your knowledge, <span className="bg-gradient-to-r from-brand-gold to-[#FFE17A] bg-clip-text text-transparent">grounded in retrieval.</span></>}
            subtitle="Kalki Ai Cloud’s RAG layer ingests your documents, embeds them, and retrieves role-scoped chunks at query time. Every answer cites the source it came from — auditable, testable and correctable."
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-10">
          {/* Sources */}
          <div className="flex flex-col gap-3">
            <div className="text-[11px] uppercase tracking-wider text-zinc-500 font-semibold">Ingested sources</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {RAG_SOURCES.map((s) => {
                const tone = TONE[s.tone] || TONE.orange
                const Icon = ICONS[s.iconKey] || Sparkles
                return (
                  <div key={s.id} className="rounded-xl border border-white/10 bg-white/[0.02] p-3 hover:bg-white/[0.03] transition-all">
                    <div className="flex items-center gap-2">
                      <span className="inline-flex h-7 w-7 items-center justify-center rounded-md border"
                        style={{ borderColor: `rgba(${tone.rgb},0.35)`, background: `rgba(${tone.rgb},0.08)`, color: tone.hex }}>
                        <Icon style={{ width: 12, height: 12 }} strokeWidth={1.8} />
                      </span>
                      <span className="text-[13px] font-semibold text-white">{s.label}</span>
                    </div>
                    <p className="text-[12px] text-zinc-400 leading-relaxed mt-1.5">{s.body}</p>
                  </div>
                )
              })}
            </div>

            <div className="mt-2 rounded-xl border border-emerald-400/25 bg-emerald-400/[0.06] p-3 text-[12.5px] text-emerald-100 flex items-start gap-2">
              <Sparkles className="mt-0.5 h-3.5 w-3.5 text-emerald-300 shrink-0" />
              <div>
                Every chunk carries provenance metadata — source, version, timestamp, ACL scope. Freshness policies re-embed on change; audit logs record every access.
              </div>
            </div>
          </div>

          {/* Retrieval pipeline */}
          <div className="rounded-3xl border border-white/10 bg-gradient-to-br from-white/[0.03] to-white/[0.01] p-5 md:p-6 backdrop-blur-xl overflow-hidden relative">
            <div className="pointer-events-none absolute inset-0 opacity-70"
              style={{ background: 'radial-gradient(60% 40% at 20% 0%, rgba(168,85,247,0.12), transparent 70%)' }} />
            <div className="relative flex flex-col gap-3">
              <div className="flex items-center gap-2">
                <Database className="h-4 w-4 text-violet-400" />
                <span className="text-[11px] font-semibold uppercase tracking-wider text-violet-300">Retrieval flow</span>
              </div>
              <h4 className="text-lg font-semibold text-white leading-tight">From your library to a grounded answer.</h4>

              <PipeStep n="01" title="Query is embedded"      body="The user’s question is turned into a dense vector using text-embedding-3-small." meta="~40ms" tone="blue" />
              <PipeArrow />
              <PipeStep n="02" title="Vector search"          body="Top-k semantic matches are pulled from the tenant’s vector store, filtered by ACL + freshness." meta="k = 6 · Pinecone" tone="purple" />
              <PipeArrow />
              <PipeStep n="03" title="Rerank + condense"       body="A cross-encoder rerank pass picks the most relevant chunks. Long chunks are condensed to fit the prompt budget." meta="rerank + de-dup" tone="cyan" />
              <PipeArrow />
              <PipeStep n="04" title="Grounded prompt + answer" body="Chunks are stitched into the prompt with citations. Model responds; citations are returned inline." meta="gpt-4o · grounded=true" tone="orange" />

              <div className="mt-2 rounded-lg border border-emerald-400/30 bg-emerald-400/[0.08] px-3 py-2 text-[12.5px] text-emerald-200 flex items-center gap-2">
                <MessageSquare className="h-3.5 w-3.5 text-emerald-300" />
                Every answer includes citations — no hallucinations, no anonymous claims.
              </div>
            </div>
          </div>
        </div>
      </Container>
    </section>
  )
}

function PipeStep({ n, title, body, meta, tone }) {
  const t = TONE[tone] || TONE.orange
  return (
    <div className="flex items-start gap-2.5 rounded-lg border border-white/10 bg-white/[0.02] p-3">
      <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-[10px] font-mono font-bold"
        style={{ borderColor: `rgba(${t.rgb},0.35)`, background: `rgba(${t.rgb},0.08)`, color: t.hex, border: `1px solid rgba(${t.rgb},0.35)` }}>{n}</span>
      <div className="flex-1 min-w-0">
        <div className="text-white font-semibold text-[13px] leading-tight">{title}</div>
        <div className="text-zinc-300 text-[12.5px] mt-0.5 leading-relaxed">{body}</div>
        <div className="text-[10px] font-mono text-zinc-500 mt-1">{meta}</div>
      </div>
    </div>
  )
}

function PipeArrow() {
  return (
    <div className="flex justify-center">
      <ArrowRight className="h-3 w-3 text-zinc-600 rotate-90" />
    </div>
  )
}
